import { getPackageConfiguration } from 'legacyfrontend/api/services/packageSuppliersServices';
import Flex from 'legacyfrontend/components/Flex';
import LoadSpinner from 'legacyfrontend/components/spinner';
import DynamicIncludesRow from 'legacyfrontend/components/packages/DynamicIncludesRow';
import { DATE_KEYS, FALLBACK_IMAGE } from 'legacyfrontend/constants';
import { adultsQtyMapper } from 'legacyfrontend/helpers/julia-mappers';
import useInformation from 'legacyfrontend/hooks/useInformation';
import { formatWithMomentFormat, utcToLocal } from 'legacyfrontend/utils/dateUtils';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { HiShare } from 'react-icons/hi';
import { useLocation } from 'react-router-dom';
import { WhatsappIcon, WhatsappShareButton } from 'react-share';
import store from 'storeClient';
import ContactFormModal from '../../components/ContactFormModal';
import ShareButton from '../../components/ui/ShareButton';
import ImageGallery from './components/ImageGallery';
import PackageHotelsInfo from './components/PackageHotelsInfo';
import PaymentSummaryRow from './components/PaymentSummaryRow';
import PackageDetailFlightsInfo from './components/FlightsInfo';
import 'styles/components/paquete-detalle.css';

const getAdultsQtyText = (adultsQty) => {
  const mappedQty = adultsQtyMapper(adultsQty);
  if (mappedQty === 0) return '-';
  if (mappedQty === 1) {
    return '1 adulto';
  } else {
    return `${mappedQty} adultos`;
  }
};

const PackageDetail = (props) => {
  const { t } = useTranslation('layout');
  const location = useLocation();
  const [configuration, setConfiguration] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        setIsLoading(true);
        const data = await getPackageConfiguration(props.match.params.id);
        setConfiguration(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching package configuration:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfiguration();
  }, [props.match.params.id]);

  const { web } = store.getState().applicationState;
  const paisId = store.getState().applicationState?.paisId || 1;
  const url = web === 'localhost' ? 'http://localhost:3000' : `https://${web}`;
  const urlComplete = `${url}/packshare/${props.match.params.id}`;
  const urlIntranet = `${url}/paquete/${props.match.params.id}`;

  const images = useMemo(() => configuration?.package?.images, [configuration?.package]);

  const [consultaPackage, setConsultaPackage] = useState();

  const sharePackage = useCallback(async () => {
    navigator.clipboard.writeText(urlComplete);
    alert(t('layout:common.link_copied'));
  }, [urlComplete]);

  const information = useInformation(
    configuration?.package?.provider_id === 1
      ? configuration?.package?.packageDescriptions?.[0]?.description
      : undefined
  );

  useEffect(() => {
    isClient &&
      window.scrollTo({
        top: 0,
      });
  }, []);

  return (
    <>
      {!!configuration && !isLoading ? (
        <Flex className='package-detail-container p-4 gap-8' vertical>
          <Flex className='boxes-wrapper'>
            <Flex className='boxes-wrapper package-information' vertical>
              <ImageGallery
                items={
                  configuration.package.provider_id === 1
                    ? [configuration?.package?.packageDestinations?.[0]?.destinationCity?.imageUrl]
                    : (images ?? FALLBACK_IMAGE)
                }
                title={configuration.package.title}
              />
              <Flex className='card-box package-information__hotels-card' vertical>
                <h3 className='package-information__description-title'>Hoteles</h3>
                <PackageHotelsInfo
                  {...configuration.package}
                  hotelInformation={configuration.hotelInformation}
                />
              </Flex>
              <Flex className='card-box package-information__flights-card' vertical>
                <h3 className='package-information__description-title'>Vuelos</h3>
                <PackageDetailFlightsInfo configuration={configuration} />
              </Flex>
              {!!information?.trim()?.length && (
                <Flex className='card-box package-information__description-card' vertical>
                  {information}
                </Flex>
              )}
            </Flex>
            <Flex className='boxes-wrapper package-details' vertical>
              <Flex className='package-details-cards' vertical>
                <Flex className='card-box package-details__summary-card' vertical>
                  <Flex className='package-details__summary-card__header with-bottom-divider'>
                    <h4 className='package-information__summary-title'>
                      {t('packages.details.package_detail')}
                    </h4>
                  </Flex>
                  <Flex
                    className='package-details__summary-card__content with-bottom-divider'
                    vertical
                  >
                    <p style={{ fontWeight: 500 }}>
                      {configuration.package.packageDestinations?.[0]?.destinationCity.nombreCiudad}
                    </p>
                    <Flex vertical>
                      <p>
                        Desde{' '}
                        {configuration.package.packageOrigins[0].originExporta.nombreCiudad.trim()}
                      </p>
                      <p>
                        Salida{' '}
                        {formatWithMomentFormat(
                          utcToLocal(
                            configuration.flight.tripInformation.find(({ order }) => order === 1)
                              .departureDate
                          ),
                          DATE_KEYS.FORMAT.DAY_SHORT
                        )}
                      </p>
                      <p style={{ fontWeight: 500 }}>
                        {getAdultsQtyText(configuration.hotelInformation[0].occupation)}
                      </p>
                    </Flex>
                  </Flex>
                  <Flex className='package-details__summary-card__footer' vertical>
                    <DynamicIncludesRow paquetesIncluidos={configuration.package.paquetesIncluidos || []} />
                  </Flex>
                </Flex>
                <Flex className='card-box package-details__payment-card' vertical>
                  <Flex className='package-details__payment-card__header with-bottom-divider'>
                    <p>Precio total</p>
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      {getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.total,
                      })}
                    </p>
                  </Flex>
                  <Flex
                    className='package-details__payment-card__content with-bottom-divider'
                    vertical
                  >
                    <PaymentSummaryRow
                      label='Precio del producto'
                      value={getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.subTotal,
                      })}
                    />
                    <PaymentSummaryRow
                      label='Impuestos y tasas'
                      value={getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.taxTotal,
                      })}
                    />
                  </Flex>
                  {paisId === 1 && (
                    <div className='package-details__payment-card__notice'>
                      <div className='title'>Nota</div>
                      <div className='content'>
                        PARA TARIFAS EXPRESADAS EN DÓLAR ESTADOUNIDENSE (USD). EN CASO DE ABONAR EN
                        PESOS (ARS) SE APLICARÁ EL TIPO DE CAMBIO CORRESPONDIENTE AL DÍA DE PAGO, Y SE
                        ADICIONARÁ LA PERCEPCIÓN CORRESPONDIENTE (RG 4815).
                      </div>
                    </div>
                  )}
                </Flex>
              </Flex>
              <Flex className='package-details-buttons' style={{ gap: '1rem' }}>
                <button
                  className='package-details-button package-details__ask-button'
                  onClick={() => {
                    setConsultaPackage({
                      ...configuration.package,
                      hotelInformation: configuration.hotelInformation,
                      total: getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.total,
                      }),
                      providerName: configuration.package.provider.name,
                      link: urlIntranet,
                    });
                  }}
                >
                  Consultar
                </button>
                <ShareButton 
                  url={urlComplete}
                  title="Compartir paquete"
                />
              </Flex>
            </Flex>
          </Flex>
          <ContactFormModal
            consultaPackage={consultaPackage}
            onClose={() => {
              setConsultaPackage(undefined);
            }}
            variant='package'
          />
        </Flex>
      ) : (
        <Flex
          style={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
          }}
        >
          <LoadSpinner />
        </Flex>
      )}
    </>
  );
};

export default PackageDetail;
